import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from 'store/store';
import {VisitDocumentThunks} from './visitDocumentThunks';
import {DocumentTemplateThunks} from 'features/documentTemplate/documentTemplateThunks';
import {DocumentTemplateProgramService} from '../../../services/documentTemplateProgramService';
import {DocumentTemplateService} from '../../../services/documentTemplateService';
import CustomSelect from 'components/inputs/CustomSelect';
import SubmitButton from 'components/SubmitButton';
import {VisitDocument} from 'types/visitDocument';
import {DocumentTemplate} from 'types/documentTemplate';
import XIcon from 'assets/images/icons/XIcon';
import buildDocumentTemplateSnapshot from '../../../utils/buildDocumentTemplateSnapshot';

interface VisitDocumentFormProps {
  closeDrawer: () => void;
  isDrawerOpen: boolean;
  visitId: string;
}

const VisitDocumentForm: React.FC<VisitDocumentFormProps> = ({
  closeDrawer,
  visitId,
  isDrawerOpen,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const visitDocument = useSelector(
    (state: RootState) => state.visitDocument.visitDocument,
  );
  const [documentTemplateOptions, setDocumentTemplateOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [documentTemplatePrograms, setDocumentTemplatePrograms] = useState<any[]>([]);
  const clientVisit = useSelector(
    (state: RootState) => state.clientVisit.clientVisit,
  );
  const [updatedVisitDocument, setUpdatedVisitDocument] =
    useState<Partial<VisitDocument>>({
      ...visitDocument,
      clientVisitId: visitId,
      status: 'missing', // Using DocumentReviewable status values
    });
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  useEffect(() => {
    const loadDocumentTemplateOptions = async () => {
      try {
        const programId = clientVisit?.programId;
        console.log('programId', programId);
        if (programId) {
          // Use DocumentTemplateProgramService to get document templates for this program
          // Include both shared (program_id = null) and program-specific (program_id = programId)
          const response = await DocumentTemplateProgramService.index(
            1,
            {
              where: {
                program_id: {
                  operator: 'in',
                  valueArray: [programId, 'null']
                }
              }
            },
            'id',
            'desc',
            100,
          );
          
          // Store the DocumentTemplateProgram results for later use
          setDocumentTemplatePrograms(response.data || []);
          
          // Create options from DocumentTemplateProgram results
          const options = response.data?.map((dtp: any) => ({
            label: dtp.documentTemplate?.name || 'Unknown Template',
            value: dtp.documentTemplateId
          })) || [];
          
          setDocumentTemplateOptions(options);
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadDocumentTemplateOptions();
  }, [dispatch, clientVisit?.programId]);

  useEffect(() => {
    const isFormValid =
      updatedVisitDocument.documentTemplateId !== undefined;

    setIsSaveDisabled(!isFormValid);
  }, [updatedVisitDocument.documentTemplateId]);

  useEffect(() => {
    setUpdatedVisitDocument({
      ...visitDocument,
      clientVisitId: visitId,
      status: visitDocument?.status || 'missing',
    });
  }, [visitDocument, visitId]);



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!updatedVisitDocument.documentTemplateId) {
      console.error('Document template ID is required');
      return;
    }

    let documentTemplateSnapshot = null;

    if (updatedVisitDocument.documentTemplateId) {
      // Find the document template from the stored DocumentTemplateProgram results
      const documentTemplateProgram = documentTemplatePrograms.find(
        (dtp: any) => dtp.documentTemplateId === updatedVisitDocument.documentTemplateId
      );
      
      if (documentTemplateProgram?.documentTemplate) {
        documentTemplateSnapshot = buildDocumentTemplateSnapshot(
          [documentTemplateProgram.documentTemplate], // Use the template from DocumentTemplateProgram
          updatedVisitDocument.documentTemplateId,
          {}, // No element values since we're not handling dynamic fields
          {}, // No fillOutOnCreation values since we're not handling dynamic fields
        );
      }

      if (!documentTemplateSnapshot) {
        console.error('Template not found');
        return;
      }
    }

    const solutionAttributes = {
      documentTemplateId: updatedVisitDocument.documentTemplateId,
      ownerId: visitDocument?.id || null, // Will be set by Rails for new records
      ownerType: 'VisitDocument',
      snapshot: documentTemplateSnapshot,
    };

    const payload = {
      ...updatedVisitDocument,
      clientVisitId: visitId,
      status: 'missing', // Using DocumentReviewable status values
      documentTemplateSolutionAttributes: solutionAttributes,
    };

    if (visitDocument?.id) {
      await dispatch(
        VisitDocumentThunks.update(
          payload as VisitDocument,
        ),
      ).then(() => closeDrawer());
    } else {
      const newVisitDocument = new VisitDocument(payload);
      await dispatch(
        VisitDocumentThunks.create(newVisitDocument),
      ).then(() => closeDrawer());
    }
  };

  useEffect(() => {
    if (!isDrawerOpen) handleClose();
  }, [isDrawerOpen]);

  const handleClose = () => {
    setUpdatedVisitDocument({
      documentTemplateId: '',
      clientVisitId: visitId,
      status: 'missing', // Using DocumentReviewable status values
    });
  };

  return (
    <div className="flex flex-col h-screen">
      <div className="flex-1 flex flex-col">
        <div className="h-auto p-6 bg-sky-50 border-b border-sky-200 flex justify-between items-start gap-2 w-full">
          <div className="flex flex-col justify-start items-start gap-1">
            <div className="text-lg font-semibold tracking-normal text-cyan-800">
              {visitDocument?.id
                ? 'Edit Document'
                : 'Create new Document'}
            </div>
            <div className="text-sm font-light tracking-normal leading-5 text-zinc-400">
              {visitDocument?.id ? 'Edit' : 'Create'} a document for
              this visit
            </div>
          </div>
          <button
            onClick={closeDrawer}
            className="text-gray-500 hover:text-gray-700 focus:outline-none pt-2">
            <XIcon />
          </button>
        </div>
        <div className="h-full px-6 pb-6">
          <div className="mt-5">
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col space-y-6 items-center mt-7">
                <div className="w-full">
                  <div className="flex flex-col gap-[0.625rem]">
                    <label
                      htmlFor="documentTemplateId"
                      className="w-full block grow shrink basis-0 text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white ">
                      Document Template
                    </label>
                           <CustomSelect
                             options={documentTemplateOptions}
                             onChange={selectedOption => {
                               setUpdatedVisitDocument({
                                 ...updatedVisitDocument,
                                 documentTemplateId: selectedOption.value,
                               });
                             }}
                             value={
                               updatedVisitDocument?.documentTemplateId
                             }
                           />

                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <footer className="flex-shrink-0 px-6 pb-4 pt-2 flex justify-end gap-5">
        <div className="border-t border-slate-200 w-full">
          <div className="flex w-full justify-center items-center gap-x-2 pb-4">
            <SubmitButton
              onClick={handleSubmit}
              label="Submit"
              disabled={isSaveDisabled}
            />
          </div>
          <div className="flex w-full justify-center items-center gap-x-2 pb-4">
            <button
              type="submit"
              onClick={closeDrawer}
              className="self-center text-sm font-light tracking-normal leading-5 text-center text-zinc-400 hover:text-cyan-400">
              Never mind
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VisitDocumentForm;
